import _styled from "styled-components";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _extends() { _extends = Object.assign ? Object.assign.bind() : function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }
import React, { useRef, useCallback, useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { VariableSizeList } from 'react-window';
import { useLocale } from '@adsk/alloy-react-locale';
import ProjectListAccountItem from './ProjectListAccountItem';
import ProjectListCategoryTitle from './ProjectListCategoryTitle';
import ProjectListProjectItem from './ProjectListProjectItem';
import ProjectListTemplateItem from './ProjectListTemplateItem';
import ProjectRowLoader from './ProjectRowLoader';
import ProjectsError from './ProjectsError';
import TemplateRowLoader from './TemplateRowLoader';
import { accountsListShape, projectsListShape } from '../helpers/propTypes';
const CATEGORY_HEADER_TYPE = 'CATEGORY_HEADER_TYPE';
const PROJECT_LOADER_TYPE = 'PROJECT_LOADER_TYPE';
const TEMPLATE_LOADER_TYPE = 'TEMPLATE_LOADER_TYPE';
const PROJECT_ERROR_TYPE = 'PROJECT_ERROR_TYPE';
const TEMPLATE_ERROR_TYPE = 'TEMPLATE_ERROR_TYPE';
const ACCOUNT_TYPE = 'ACCOUNT_TYPE';
const PROJECT_TYPE = 'PROJECT_TYPE';
const TEMPLATE_TYPE = 'TEMPLATE_TYPE';
const LIST_MAX_HEIGHT = 450;
const ListItem = props => {
  const {
    item
  } = props;
  const type = item.type;
  switch (type) {
    case CATEGORY_HEADER_TYPE:
      return /*#__PURE__*/React.createElement(ProjectListCategoryTitle, {
        item: item
      });
    case PROJECT_LOADER_TYPE:
      return /*#__PURE__*/React.createElement(ProjectRowLoader, null);
    case TEMPLATE_LOADER_TYPE:
      return /*#__PURE__*/React.createElement(TemplateRowLoader, null);
    case PROJECT_ERROR_TYPE:
      return /*#__PURE__*/React.createElement(ProjectsError, {
        retry: props.projectsRetry,
        projectsCount: props.projectsCount
      });
    case TEMPLATE_ERROR_TYPE:
      return /*#__PURE__*/React.createElement(ProjectsError, {
        retry: props.projectsRetry,
        projectsCount: props.projectsCount,
        variant: ProjectsError.VARIANTS.TEMPLATES
      });
    case ACCOUNT_TYPE:
      return /*#__PURE__*/React.createElement(ProjectListAccountItem, _extends({}, props, {
        item: item
      }));
    case PROJECT_TYPE:
      return /*#__PURE__*/React.createElement(ProjectListProjectItem, _extends({}, props, {
        item: item
      }));
    case TEMPLATE_TYPE:
      return /*#__PURE__*/React.createElement(ProjectListTemplateItem, _extends({}, props, {
        item: item
      }));
    default:
      console.error('VirtualizedProjectList - Unknown item type: ', type);
      return null;
  }
};
ListItem.propTypes = {
  item: PropTypes.shape({
    type: PropTypes.string
  }),
  projectsRetry: PropTypes.func,
  projectsCount: PropTypes.number,
  onAccountClick: PropTypes.func,
  onProjectClick: PropTypes.func
};
const getDefaultSizeByType = type => {
  switch (type) {
    case CATEGORY_HEADER_TYPE:
      return 42;
    case PROJECT_LOADER_TYPE:
      return 240;
    case TEMPLATE_LOADER_TYPE:
      return 180;
    default:
      return 64;
  }
};
const getSizeCacheKey = item => {
  const type = item.type;
  switch (type) {
    case ACCOUNT_TYPE:
      return "".concat(type, "_").concat(item.account_id);
    case PROJECT_TYPE:
    case TEMPLATE_TYPE:
      return "".concat(type, "_").concat(item.id);
    default:
      return type;
  }
};
const SizeDetector = _ref => {
  let {
    setSize,
    item,
    index,
    children
  } = _ref;
  const root = useRef(null);
  useEffect(() => {
    var _root$current;
    const {
      height
    } = ((_root$current = root.current) === null || _root$current === void 0 ? void 0 : _root$current.getBoundingClientRect()) || {};
    if (height !== getDefaultSizeByType(item.type)) {
      setSize(item, height, index);
    }
  }, [setSize, item, index]);
  return /*#__PURE__*/React.createElement("div", {
    ref: root
  }, children);
};
SizeDetector.propTypes = {
  setSize: PropTypes.func,
  index: PropTypes.number,
  item: PropTypes.object,
  children: PropTypes.node
};
const VirtualizedProjectList = _ref2 => {
  let {
    accounts,
    currentAccountId,
    projects,
    currentProjectId,
    templates,
    currentProductId,
    projectsLoading,
    projectsError,
    projectsRetry,
    onClose,
    onAccountClick,
    onProjectClick,
    handleAnalyticOnClick
  } = _ref2;
  const {
    T
  } = useLocale(_ref3 => {
    let {
      locale
    } = _ref3;
    return require("../i18n/".concat(locale, ".json"));
  });
  const sizeMap = useRef({});
  const listRef = useRef(null);
  const [_, triggerRerender] = useState();
  useEffect(() => {
    var _listRef$current;
    // Reset cache as the current visible item heights might be different.
    (_listRef$current = listRef.current) === null || _listRef$current === void 0 ? void 0 : _listRef$current.resetAfterIndex(0);
  }, [accounts, projects, templates, projectsLoading]);
  const items = useMemo(() => {
    const allItems = [];
    if (accounts && accounts.length) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        title: T.translate('ACCOUNTS')
      });
      accounts.forEach(account => {
        allItems.push(_objectSpread(_objectSpread({}, account), {}, {
          type: ACCOUNT_TYPE
        }));
      });
    }
    const showProjectsHeader = projects && projects.length || projectsError;
    if (showProjectsHeader) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        key: 'projects',
        title: T.translate('PROJECTS')
      });
    }
    if (projects && projects.length) {
      projects.forEach(project => {
        allItems.push(_objectSpread(_objectSpread({}, project), {}, {
          type: PROJECT_TYPE
        }));
      });
      if (projectsLoading) {
        allItems.push({
          type: PROJECT_LOADER_TYPE,
          key: 'projects_loader'
        });
      }
    }
    if (projectsError) {
      allItems.push({
        type: PROJECT_ERROR_TYPE,
        key: 'projects_error'
      });
    }
    if (templates && templates.length) {
      allItems.push({
        type: CATEGORY_HEADER_TYPE,
        key: 'templates',
        title: T.translate('TEMPLATES')
      });
      templates.forEach(template => {
        allItems.push(_objectSpread(_objectSpread({}, template), {}, {
          type: TEMPLATE_TYPE
        }));
      });
      if (projectsLoading) {
        allItems.push({
          type: TEMPLATE_LOADER_TYPE,
          key: 'templates_loader'
        });
      } else if (projectsError) {
        allItems.push({
          type: TEMPLATE_ERROR_TYPE,
          key: 'templates_error'
        });
      }
    }
    return allItems;
  }, [T, projects, accounts, templates, projectsLoading, projectsError]);
  const getSizeByItem = useCallback(item => {
    const cacheKey = getSizeCacheKey(item);
    return sizeMap.current[cacheKey] || getDefaultSizeByType(item.type);
  }, []);
  const getSizeByIndex = useCallback(index => {
    return getSizeByItem(items[index]);
  }, [items, getSizeByItem]);
  const setSize = useCallback((item, size, index) => {
    const cacheKey = getSizeCacheKey(item);
    const prevSize = getSizeByItem(item);
    if (prevSize !== size) {
      var _listRef$current2;
      sizeMap.current = _objectSpread(_objectSpread({}, sizeMap.current), {}, {
        [cacheKey]: size
      });
      index !== undefined && ((_listRef$current2 = listRef.current) === null || _listRef$current2 === void 0 ? void 0 : _listRef$current2.resetAfterIndex(index, false));
      triggerRerender(undefined);
    }
  }, [getSizeByItem]);
  const itemsTotalHeight = useMemo(() => {
    return items.reduce((acc, item) => acc + getSizeByItem(item), 0);
  }, [items, getSizeByItem]);
  return /*#__PURE__*/React.createElement(_StyledDiv, {
    $_css: [{
      minHeight: 120,
      maxHeight: LIST_MAX_HEIGHT,
      overflow: 'auto'
    }]
  }, /*#__PURE__*/React.createElement(VariableSizeList, {
    height: Math.min(LIST_MAX_HEIGHT, itemsTotalHeight),
    width: "100%",
    itemCount: items.length,
    itemSize: getSizeByIndex,
    itemData: items,
    ref: listRef
  }, _ref4 => {
    let {
      index,
      style
    } = _ref4;
    return (
      /*#__PURE__*/
      // Using inline style explicitly to fix Styled Component warnings of creating the same style again and again
      React.createElement("div", {
        style: style
      }, /*#__PURE__*/React.createElement(SizeDetector, {
        setSize: setSize,
        index: index,
        item: items[index]
      }, /*#__PURE__*/React.createElement(ListItem, {
        index: index,
        item: items[index],
        currentAccountId: currentAccountId,
        currentProjectId: currentProjectId,
        currentProductId: currentProductId,
        onClose: onClose,
        projectsRetry: projectsRetry,
        projectsCount: projects.length,
        onAccountClick: onAccountClick,
        onProjectClick: onProjectClick,
        handleAnalyticOnClick: handleAnalyticOnClick
      })))
    );
  }));
};
VirtualizedProjectList.displayName = 'VirtualizedProjectList';
VirtualizedProjectList.propTypes = {
  /** Available accounts to select from */
  accounts: accountsListShape,
  /** current selected account id */
  currentAccountId: PropTypes.string,
  /** Available projects to select from */
  projects: projectsListShape,
  /** Available templates */
  templates: projectsListShape,
  /** current selected project id */
  currentProjectId: PropTypes.string,
  /** current product ID, to check if project has entitlements for or not */
  currentProductId: PropTypes.string,
  /** show projects loader */
  projectsLoading: PropTypes.bool,
  /** is project entitlements ended in error */
  projectsError: PropTypes.bool,
  /** retry loading project entitlements */
  projectsRetry: PropTypes.func,
  /** menu close handler */
  onClose: PropTypes.func,
  /** account click handler */
  onAccountClick: PropTypes.func,
  /** project click handler */
  onProjectClick: PropTypes.func
};
export default VirtualizedProjectList;
var _StyledDiv = _styled("div").withConfig({
  displayName: "VirtualizedProjectList___StyledDiv",
  componentId: "sc-1snvy8x-0"
})(["", ""], p => p.$_css);
//# sourceMappingURL=VirtualizedProjectList.js.map